import React, { useMemo, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TableSortLabel,
  Paper,
} from "@mui/material";
import { formatPen } from "../../../../utils/finance/format";

function desc(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => desc(a, b, orderBy)
    : (a, b) => -desc(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilized = array.map((el, index) => [el, index]);
  stabilized.sort((a, b) => {
    const cmp = comparator(a[0], b[0]);
    if (cmp !== 0) return cmp;
    return a[1] - b[1];
  });
  return stabilized.map((el) => el[0]);
}

export function FinanceSalesTable({ rows = [] }) {
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("issue_date");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const normalizedRows = useMemo(() => {
    return (rows || []).map((r) => ({
      ...r,
      client: r.client ?? "-",
      total_pen_num: Number(r.total_pen || 0),
      paid_pen_num: Number(r.paid_pen || 0),
      pending_pen_num: Number(r.pending_pen || 0),
      invoiced_text: r.invoiced ? "Sí" : "No",
    }));
  }, [rows]);

  const headCells = [
    { id: "issue_date", label: "Fecha" },
    { id: "client", label: "Cliente" },
    { id: "service_type", label: "Servicio" },
    { id: "acquisition_channel", label: "Canal" },
    { id: "status", label: "Estado" },
    { id: "invoiced_text", label: "Fact." },
    { id: "total_pen_num", label: "Total" },
    { id: "paid_pen_num", label: "Pagado" },
    { id: "pending_pen_num", label: "Pendiente" },
  ];

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const visibleRows = useMemo(() => {
    const sorted = stableSort(normalizedRows, getComparator(order, orderBy));
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [normalizedRows, order, orderBy, page, rowsPerPage]);

  return (
    <Card sx={{ borderRadius: 6 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Ventas
        </Typography>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                {headCells.map((h) => (
                  <TableCell key={h.id} sortDirection={orderBy === h.id ? order : false}>
                    <TableSortLabel
                      active={orderBy === h.id}
                      direction={orderBy === h.id ? order : "asc"}
                      onClick={() => handleRequestSort(h.id)}
                    >
                      {h.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {visibleRows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.issue_date}</TableCell>
                  <TableCell>{r.client}</TableCell>
                  <TableCell>{r.service_type}</TableCell>
                  <TableCell>{r.acquisition_channel}</TableCell>
                  <TableCell>{r.status}</TableCell>
                  <TableCell>{r.invoiced_text}</TableCell>
                  <TableCell>{formatPen(r.total_pen_num)}</TableCell>
                  <TableCell>{formatPen(r.paid_pen_num)}</TableCell>
                  <TableCell>{formatPen(r.pending_pen_num)}</TableCell>
                </TableRow>
              ))}

              {visibleRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                    No hay ventas para el rango seleccionado.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={normalizedRows.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Filas"
          />
        </TableContainer>
      </CardContent>
    </Card>
  );
}
